/**
 * @file storage.js
 * @location src/utils/storage.js
 * @role localStorage 영속화 헬퍼 (저장 · 읽기 · 스키마 마이그레이션 호출)
 * ※ 온라인 모드에선 Supabase 가 SoT — 이 localStorage 영속은 캐시/리로드 큐 복원용. (재)연결 시 HYDRATE 가 덮어씀(useSupabaseStore 참조).
 *
 * 키: 'workdo_v2' (LS_KEY 는 그대로, _version 필드로 스키마 버전 트래킹)
 * 마이그레이션 본체는 ./migrations.js
 * JSON Export/Import 는 ./storageBackup.js
 *
 * saveToStorage(data)   → localStorage 에 직렬화 저장 (실패 시 console.error)
 * loadFromStorage()     → 역직렬화 후 유효성 검사 + 프로토타입 오염 방어. 손상 시 null
 *
 * 견고화 정책:
 *   - silent catch 금지. console.error 로 가시화 (사용자 영향 케이스 — prod 에서도 진단 가능)
 *   - JSON.parse 에 reviver 적용 — __proto__ / constructor / prototype 키 거부
 *   - 구조 검증 — 필수 필드(tasks) 의 타입을 정의된 helper 로 확인
 */
import { migrate } from './migrations.js';

const LS_KEY = 'workdo_v2';
export const SCHEMA_VERSION = 1;

// saveToStorage / loadFromStorage / exportToJSON 모두 동일한 영속 필드 화이트리스트를 사용
// 폐기 razor 로 제외된 키 (화이트리스트 밖이라 저장 시 자연 소멸):
// ※ approvalDelegations — 신청(품의) razor ⑮ 폐기
// ※ weeklyReports — 주보 razor ⑰ 폐기
// ※ reports / reportPolicy — 일보 razor ⑱ 폐기 (「일하면 그게 보고」)
// ※ departments — 부서 razor ⑲ 폐기 (「단일 매니저 팀」)
// ※ permissions / orgLevels — 직급 razor ⑳ 폐기 (매니저/팀원 이분)
// ※ todos / timelineCore — ToDo·Timeline razor ㉑ 폐기
export const PERSISTENT_KEYS = [
  'tasks', 'deletedTasks', 'darkMode',
  'members',
  'taskTemplates',
  'companyName',
  'uiPrefs',
  'onboarded',
  'outbox',   // sync 실패 큐 — src/utils/outbox.js
];

/** plain object 판정 — null, Array, Date 등은 false */
export function isPlainObject(v) {
  return v != null && typeof v === 'object' && !Array.isArray(v);
}

/**
 * JSON.parse 에 적용할 reviver — 프로토타입 오염 방어.
 * `__proto__`, `constructor`, `prototype` 키는 무조건 제거.
 */
function safeReviver(key, value) {
  if (key === '__proto__' || key === 'constructor' || key === 'prototype') return undefined;
  return value;
}

/** raw JSON → 객체 또는 오류 시 null + console.error */
export function safeJsonParse(raw, contextLabel = 'storage') {
  try {
    return JSON.parse(raw, safeReviver);
  } catch (err) {
    console.error(`[${contextLabel}] JSON parse failed:`, err);
    return null;
  }
}

/** 최소 구조 검증 — 손상/악의적 페이로드를 탐지 */
function isValidPersistedShape(data) {
  if (!isPlainObject(data))                return false;
  if (!Array.isArray(data.tasks))          return false;
  // _version 은 옵셔널(없으면 v0). 숫자가 아니면 거부.
  if (data._version != null && typeof data._version !== 'number') return false;
  return true;
}

/**
 * 1회성 — 기존 localStorage 키 workdo_section_* 를 uiPrefs.sectionOpen 로 흡수하고 key 정리.
 * 브라우저(localStorage 가능) 만 동작.
 * (workdo_returndigest_* 흡수는 returnDigest razor ㉓ 후속 폐기(v57) · workdo_ct_s/e[timelineCore] 는 ㉑ 로 폐기)
 */
function importLegacyUiPrefs(data) {
  if (typeof localStorage === 'undefined') return data;
  let changed = false;
  const next = {
    ...data,
    uiPrefs: {
      sectionOpen: { ...(data.uiPrefs?.sectionOpen ?? {}) },
    },
  };
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith('workdo_section_')) keys.push(k);
    }
    for (const k of keys) {
      const v = localStorage.getItem(k);
      const rest = k.slice('workdo_section_'.length);
      const idx = rest.indexOf('_');
      if (idx > 0) {
        const uid = rest.slice(0, idx);
        const sectionKey = rest.slice(idx + 1);
        if (!next.uiPrefs.sectionOpen[uid]) next.uiPrefs.sectionOpen[uid] = {};
        next.uiPrefs.sectionOpen[uid][sectionKey] = v === '1';
      }
      localStorage.removeItem(k);
      changed = true;
    }
  } catch (err) {
    console.error('[storage] importLegacyUiPrefs failed:', err);
  }
  return changed ? next : data;
}

/**
 * localStorage 저장 — 성공 시 true, 실패 시 false 반환.
 * 실패 시 `storage:save-failed` CustomEvent 발행 (detail.isQuota 로 분기).
 * App 레벨 listener 가 토스트로 사용자에게 알림.
 */
export function saveToStorage(data) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify({ ...data, _version: SCHEMA_VERSION }));
    return true;
  } catch (err) {
    // QuotaExceededError / private mode 거부 등
    console.error('[storage] save failed:', err);
    const isQuota = err?.name === 'QuotaExceededError'
      || err?.name === 'NS_ERROR_DOM_QUOTA_REACHED'
      || err?.code === 22 || err?.code === 1014;
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      try {
        window.dispatchEvent(new CustomEvent('storage:save-failed', {
          detail: { isQuota, error: err },
        }));
      } catch { /* CustomEvent 미지원 환경 — 무시 */ }
    }
    return false;
  }
}

export function loadFromStorage() {
  let raw;
  try {
    raw = localStorage.getItem(LS_KEY);
  } catch (err) {
    console.error('[storage] localStorage.getItem failed:', err);
    return null;
  }
  if (!raw) return null;

  const parsed = safeJsonParse(raw, 'storage.load');
  if (parsed == null) return null;

  if (!isValidPersistedShape(parsed)) {
    console.error('[storage] load rejected — invalid shape (tasks)');
    return null;
  }

  let data = parsed;
  const wasOlder = (data._version ?? 0) < SCHEMA_VERSION;
  if (wasOlder) {
    try {
      data = migrate(data);
      // 베이스라인(v1) 미만 — 업그레이드 경로 없음 → null 반환 → 기본 시드로 초기화(재온보딩)
      if (data == null) return null;
      data = importLegacyUiPrefs(data);
      saveToStorage(data);
    } catch (err) {
      console.error('[storage] migration failed:', err);
      return null;
    }
  }
  return data;
}

// re-export — 외부 진입점 호환 유지 (data/constants.js 가 storage.js 의 모든 export 를 wildcard 로 흘림)
export { exportToJSON, importFromJSON } from './storageBackup.js';
